import React, { Component } from "react";
import { Col, Row } from "reactstrap";
import { HoveredWidget } from "../components/HoveredWidget";
import { AppCard, LoadingDots, PageLayout } from "../components/ui";
import Users from "./Users/Users";
import MerchantPaymentMethods from "./Merchants/MerchantPaymentMethods";
import { merchantWalletApiService } from "../services/api/merchants/MerchantWalletApiService";
import { Wallets } from "../models/FinancialData";
import { storageService } from "../services/StorageService";
import { DepositViaPaymentMethodDialog } from "./Banking/DepositViaPaymentMethodDialog";
import { WithdrawViaPaymentMethodDialog } from "./Banking/WithdrawViaPaymentMethodDialog";
import { LayoutDashboard, MinusCircle, PlusCircle, Banknote, Scale, TrendingUp } from "lucide-react";
import { isMerchantDashboardUpdateAllowed, isMerchantPaymentMethodUpdateAllowed } from "../authGuard";
import AllTransactions from "./Operations/AllTransactions";

import WebPagerView from "./Merchants/WebPagerView";
import { TransferFundsDialogue } from "./Banking/TransferFundsDialogue";
import { ExtendedSelectItem } from "../models/SelectItem";
import { operatorsApiService } from "../services/api/users/OperatorApiService";
import { toastService } from "../services/toastService";
import EventEmitter, { Events } from "../services/eventemitter";
import { OperatorDataModel } from "../models/User";
import DepositFromPaymentMethods from "./Banking/DepositFromPaymentMethods";

interface MerchantDashboardState {
    showDepositDialog: boolean;
    showWithdrawDialog: boolean;
    showTransferDialog: boolean;
    showDirectDebitDialog: boolean;
    wallets: Wallets[];
    isLoading: boolean;
    isWalletBalanceLoading: boolean;
    merchantId: number;
    showBalance: boolean;
    sourceWallets: Wallets[];
    sourceAccountsOptions: ExtendedSelectItem[];
    loggedUserData: OperatorDataModel | null;
    refreshCounter: number
}

export default class MerchantDashboard extends Component<Record<string, never>, MerchantDashboardState> {
    constructor(props: Record<string, never> | Readonly<Record<string, never>>) {
        super(props);

        this.state = {
            showDepositDialog: false,
            showDirectDebitDialog: false,
            showTransferDialog: false,
            showWithdrawDialog: false,
            wallets: [],
            refreshCounter: 0,
            isLoading: true,
            isWalletBalanceLoading: true,
            merchantId: storageService.getLoggedInUserMerchantId(),
            showBalance: false,
            sourceWallets: [],
            sourceAccountsOptions: [],
            loggedUserData: null,
        };
        EventEmitter.on(Events.OnUserDataUpdate, this.updateUserData);
    }

    async componentDidMount(): Promise<void> {
        await this.updateUserData();
        await this.onLoad();
    }

    updateUserData = async () => {
        const userData = await operatorsApiService.get();
        if (!userData.success) {
            return;
        }
        this.setState({ loggedUserData: userData.data });
    };

    onLoad = async () => {
        this.setState(prevState => ({
            isLoading: true,
            refreshCounter: prevState.refreshCounter + 1
        }));
        const walletsResult = await merchantWalletApiService.getWallets(this.state.merchantId);

        if (!walletsResult.success) {
            this.setState({ isLoading: false, isWalletBalanceLoading: false });
            toastService.error(walletsResult.message);
            return;
        }

        const balanceResult = (
            await Promise.all(
                walletsResult.data.map((item) =>
                    merchantWalletApiService.getWalletBalance({ merchantId: this.state.merchantId, accountNumber: item.id }),
                ),
            )
        )
            .filter((a) => a.success)
            .map((a) => a.data);

        const wallets = walletsResult.data.map((w) => {
            w.balance = balanceResult.find((a) => a.id == w.id)?.balance ?? 0;
            w.blockedAmount = balanceResult.find((a) => a.id == w.id)?.blockedAmount ?? 0;

            return w;
        });

        const sourceAccountOptions = wallets.map((wallet) => ({
            label: wallet.ibanAccountNumber,
            value: wallet.ibanAccountNumber,
            details: {
                fullname: this.state.loggedUserData?.firstName + " " + this.state.loggedUserData?.lastName,
                subtitle: this.state.loggedUserData?.email,
                icons: "wallet-outline",
                currency: wallet.currency,
            },
        })) as ExtendedSelectItem[];

        this.setState((prevState) => ({
            ...prevState,
            sourceAccountsOptions: sourceAccountOptions,
            sourceWallets: walletsResult.data,
            wallets: wallets,
            isWalletBalanceLoading: false,
            isLoading: false,
        }));
    };

    renderWallets = (wallet: Wallets, index: number) => {
        return (
            <Col>
                <HoveredWidget
                    textClass=""
                    icon={wallet.currency}
                    description={wallet.ibanAccountNumber}
                    title={`${wallet.balance}`}
                    subHeading={wallet.currency}
                    isLoading={this.state.isWalletBalanceLoading}
                />
            </Col>
        );
    };

    renderWidgets = () => {
        if (this.state.isLoading) {
            return (
                <LoadingDots className="py-8" />
            );
        }
        return (
            <div className="mt-4">
                <AppCard>
                    <AppCard.Body>
                        <Row className={`p-4 flex-nowrap scroll-smooth ${this.state.wallets.length > 1 ? "overflow-x-auto" : "overflow-x-hidden"}`}>
                            {this.state.wallets.map(this.renderWallets)}
                        </Row>
                    </AppCard.Body>
                </AppCard>
                <Row>
                    <Col>
                        <HoveredWidgetWrapper>
                            <HoveredWidget
                                textClass="warning"
                                small={true}
                                icon={MinusCircle}
                                title="Transfer"
                                subHeading="_"
                                onClick={this.onTransfer}
                            />
                        </HoveredWidgetWrapper>
                    </Col>
                    <Col>
                        <HoveredWidgetWrapper>
                            <HoveredWidget
                                textClass="info"
                                small={true}
                                icon={PlusCircle}
                                title="Deposit"
                                subHeading="_"
                                onClick={this.onDeposit}
                            />
                        </HoveredWidgetWrapper>
                    </Col>
                    <Col>
                        <HoveredWidgetWrapper>
                            <HoveredWidget
                                textClass="danger"
                                small={true}
                                icon={MinusCircle}
                                title="Withdraw"
                                subHeading="_"
                                onClick={this.onWithdraw}
                            />
                        </HoveredWidgetWrapper>
                    </Col>

                    {/*
                        <Col style={{ maxWidth: '300px' }}>
                            <HoveredWidgetWrapper>
                                <HoveredWidget textClass="primary" small={true} icon="lnr-location" title="Direct Debit" subHeading="_" onClick={this.onDirectDebit} />
                            </HoveredWidgetWrapper>
                        </Col> */}
                </Row>
            </div>
        );
    };

    onBalance = () => {
        this.setState({ showBalance: true });
    };

    onDeposit = () => {
        this.setState({ showDepositDialog: true });
    };

    onWithdraw = () => {
        this.setState({ showWithdrawDialog: true });
    };

    onTransfer = () => {
        this.setState({ showTransferDialog: true });
    };

    onDirectDebit = () => {
        this.setState({ showDirectDebitDialog: true });
    };

    render() {
        return (
            <>
                {this.state.showTransferDialog && (
                    <TransferFundsDialogue
                        loggedUserData={this.state.loggedUserData}
                        sourceAccountsOptions={this.state.sourceAccountsOptions}
                        sourceWallets={this.state.sourceWallets}
                        merchantId={this.state.merchantId}
                        reloadData={() => this.onLoad()}
                        onHide={() => { this.setState({ showTransferDialog: false }) }}

                    />
                )}
                {this.state.showDepositDialog && (
                    <DepositFromPaymentMethods OnSuccess={() => {this.onLoad() }} onHide={() => { this.setState({ showDepositDialog: false }) }} />
                )}
                {this.state.showWithdrawDialog && (
                    <WithdrawViaPaymentMethodDialog
                        loggedUserData={this.state.loggedUserData}
                        sourceAccountsOptions={this.state.sourceAccountsOptions}
                        sourceWallets={this.state.sourceWallets}
                        merchantId={this.state.merchantId}
                        reloadData={() => this.onLoad()}
                        onHide={() => { this.setState({ showWithdrawDialog: false }) }}

                    />
                )}
                <PageLayout heading="Dashboard" icon={LayoutDashboard}>
                    {(isMerchantDashboardUpdateAllowed() || isMerchantPaymentMethodUpdateAllowed()) && (
                        this.renderWidgets()
                    )}

                    {this.state.loggedUserData && this.state.loggedUserData.flexCubeBranch && (
                        <AllTransactions merchantBranch={this.state.loggedUserData.flexCubeBranch} merchantId={this.state.merchantId} refreshTrigger={this.state.refreshCounter} />
                    )}

                    <AppCard>
                        <AppCard.Header title="Payment Methods" />
                        <MerchantPaymentMethods merchantId={this.state.merchantId} viewOnly={false} />
                    </AppCard>
                </PageLayout>
            </>
        );
    }
}

function HoveredWidgetWrapper({ ...props }) {
    return (
        <div className="mb-3">
            {props.children}
        </div>
    );
}
